import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { customers } from '@/data/customer'

import Marquee from '../common/marquee'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface LogoItem {
  title: string
  icon?: React.ReactNode
  image?: string
  alt?: string
  href?: string
  linkTitle?: string
}

interface CorporateCustomerProps {
  className?: string
  /** Section heading */
  heading?: string
  /** Section subheading */
  subheading?: string
  /** Heading text color */
  headingColor?: string
  /** Subheading text color */
  subheadingColor?: string
  /** Override default logos (uses customers data if not provided) */
  logos?: LogoItem[]
  /** Logo source: 'default' uses built-in customers, 'custom' uses logos array */
  logoSource?: 'default' | 'custom'
  /** Logo size */
  logoSize?: string
  /** Background color */
  bgColor?: string
  /** Text color */
  textColor?: string
  /** Whether to display the section heading. */
  showHeading?: boolean
  /** Whether logo movement runs automatically. */
  autoPlay?: boolean
  /** Duration of one marquee cycle in seconds. */
  durationSeconds?: number
  /** Whether movement pauses while hovered. */
  pauseOnHover?: boolean
  /** Visible movement axis. */
  movementDirection?: 'horizontal' | 'vertical'
  /** Whether movement runs in reverse. */
  reverse?: boolean
  /** Whether edge fading is shown. */
  fade?: boolean
  /** Number of duplicated logo tracks. */
  numberOfCopies?: number
  /** Gap between logos in pixels. */
  gapPx?: number
}

export const corporateCustomerSchema: SectionElementSchema = {
  componentType: 'CorporateCustomer',
  label: 'Corporate Customer Logos',
  category: 'content',
  description: 'Display customer/partner logos in a marquee',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      helpText: 'Section title (supports HTML)',
      default: '',
    },
    {
      key: 'subheading',
      type: 'textarea',
      label: 'Subheading',
      helpText: 'Optional subtitle',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      default: '#ffffff',
    },
    {
      key: 'subheadingColor',
      type: 'color',
      label: 'Subheading Color',
      default: 'rgba(255,255,255,0.8)',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      default: '#991b1b',
    },
    {
      key: 'textColor',
      type: 'color',
      label: 'Text Color',
      helpText: 'Color applied to all text in the section',
      default: '#ffffff',
    },
    {
      key: 'logoSource',
      type: 'select',
      label: 'Logo Source',
      helpText: 'Use built-in default logos or provide custom logos',
      options: [
        { label: 'Default (Built-in)', value: 'default' },
        { label: 'Custom', value: 'custom' },
      ],
      default: 'default',
    },
    {
      key: 'logoSize',
      type: 'select',
      label: 'Logo / Icon Size',
      helpText: 'Control the display size of logos and icons',
      options: [
        { label: 'Small (24px)', value: 'h-6' },
        { label: 'Medium (32px)', value: 'h-8' },
        { label: 'Large (40px)', value: 'h-10' },
        { label: 'Extra Large (48px)', value: 'h-12' },
        { label: 'Super Large (60px)', value: 'h-16' },
      ],
      default: 'h-8',
    },
    { key: 'showHeading', type: 'boolean', label: 'Show Heading', default: true },
    { key: 'autoPlay', type: 'boolean', label: 'Auto Play', default: true },
    {
      key: 'durationSeconds',
      type: 'number',
      label: 'Animation Duration (seconds)',
      min: 5,
      max: 120,
      default: 25,
    },
    { key: 'pauseOnHover', type: 'boolean', label: 'Pause on Hover', default: true },
    {
      key: 'movementDirection',
      type: 'select',
      label: 'Movement Direction',
      options: [
        { label: 'Horizontal', value: 'horizontal' },
        { label: 'Vertical', value: 'vertical' },
      ],
      default: 'horizontal',
    },
    { key: 'reverse', type: 'boolean', label: 'Reverse Direction', default: false },
    { key: 'fade', type: 'boolean', label: 'Fade Edges', default: true },
    { key: 'numberOfCopies', type: 'number', label: 'Loop Copies', min: 1, max: 10, default: 5 },
    { key: 'gapPx', type: 'number', label: 'Logo Gap (px)', min: 0, max: 120, default: 48 },
    {
      key: 'logos',
      type: 'array',
      label: 'Custom Logos',
      helpText: 'Custom logo list (only used when Logo Source is "Custom")',
      default: [],
      children: [
        { key: 'title', type: 'text', label: 'Logo Title', required: true },
        { key: 'image', type: 'image', label: 'Logo Image URL', required: true },
        { key: 'alt', type: 'text', label: 'Alternative Text' },
        { key: 'href', type: 'text', label: 'Link URL' },
        { key: 'linkTitle', type: 'text', label: 'Link Title' },
      ],
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

sectionSchemaRegistry.register(corporateCustomerSchema)

export default function CorporateCustomer({
  className,
  heading,
  subheading,
  headingColor = '#ffffff',
  subheadingColor = 'rgba(255,255,255,0.8)',
  logos,
  logoSource = 'default',
  logoSize = 'h-8',
  bgColor = '#991b1b',
  textColor = '#ffffff',
  showHeading = true,
  autoPlay = true,
  durationSeconds = 25,
  pauseOnHover = true,
  movementDirection = 'horizontal',
  reverse = false,
  fade = true,
  numberOfCopies = 5,
  gapPx = 48,
}: CorporateCustomerProps) {
  const { text } = useLocale()
  const displayHeading = heading || text.CORPORATE_CUSTOMER.TITLE
  const useCustomLogos = logoSource === 'custom' && logos && logos.length > 0
  const displayLogos: LogoItem[] = useCustomLogos ? logos : customers

  return (
    <section className={cn('py-10', className)} style={{ backgroundColor: bgColor, color: textColor }}>
      <div className="container">
        {showHeading && (
          <h2
            className="text-center text-base font-medium"
            style={{ color: headingColor }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(displayHeading) }}
          />
        )}
        {subheading && (
          <p
            className="text-center text-sm mt-2"
            style={{ color: subheadingColor }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(subheading) }}
          />
        )}
        <div className="relative mt-8 overflow-hidden">
          {fade && (
            <>
              <div
                data-testid="corporate-customer-left-fade"
                className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
                style={{
                  background: `linear-gradient(to right, ${bgColor}, ${bgColor}cc, transparent)`,
                }}
              />
              <div
                data-testid="corporate-customer-right-fade"
                className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
                style={{
                  background: `linear-gradient(to left, ${bgColor}, ${bgColor}cc, transparent)`,
                }}
              />
            </>
          )}
          <Marquee
            fade={fade}
            pauseOnHover={pauseOnHover}
            direction={movementDirection === 'horizontal' ? 'vertical' : 'horizontal'}
            reverse={reverse}
            numberOfCopies={numberOfCopies}
            autoPlay={autoPlay}
            durationSeconds={durationSeconds}
            gapPx={gapPx}
          >
            {(displayLogos as LogoItem[]).map((item, index) => {
              const accessibleName = item.alt || item.title
              const logo =
                useCustomLogos && item.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={item.image} alt={accessibleName} className={cn(logoSize, 'w-auto object-contain')} />
                ) : (
                  <span
                    role="img"
                    aria-label={accessibleName}
                    className={cn(logoSize, '[&_svg]:h-full [&_svg]:w-auto')}
                  >
                    {'icon' in item && item.icon}
                  </span>
                )

              return (
                <div className="flex flex-col items-center p-4 transition-transform" key={`${item.title}-${index}`}>
                  {useCustomLogos && item.href ? (
                    <a
                      href={item.href}
                      aria-label={accessibleName}
                      title={item.linkTitle}
                      target={/^https?:\/\//.test(item.href) ? '_blank' : undefined}
                      rel={/^https?:\/\//.test(item.href) ? 'noopener noreferrer' : undefined}
                      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                    >
                      {logo}
                    </a>
                  ) : (
                    logo
                  )}
                </div>
              )
            })}
          </Marquee>
        </div>
      </div>
    </section>
  )
}
